import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CREDIT_BLOCK_CLIENT_DEFAULT_SECTION,
  creditBlockClientSectionToTabId,
  creditBlockClientTabIdToSection,
  isKnownCreditBlockClientSection,
} from '../creditBlockClientDetailUrl';

export function useCreditBlockClientDetailUrl(options: {
  clientId: string;
  setActiveTab: (tabId: string) => void;
  setTabNavigationRequest: (req: { tabId: string; nonce: number } | null) => void;
  setSelectedSubscriptionId: (id: string | null) => void;
}) {
  const { clientId, setActiveTab, setTabNavigationRequest, setSelectedSubscriptionId } = options;
  const navigate = useNavigate();
  const { clientSection } = useParams<{ clientId?: string; clientSection?: string }>();
  const [searchParams] = useSearchParams();
  const urlSectionAppliedRef = useRef<string | null>(null);

  const syncTabSlugInUrl = useCallback(
    (tabId: string) => {
      const slug = creditBlockClientTabIdToSection(tabId);
      if (!slug) return;
      if (slug === clientSection) return;
      const cid = encodeURIComponent(clientId);
      const next = new URLSearchParams(searchParams);
      if (slug !== 'subscription') next.delete('subscriptionId');
      const qs = next.toString();
      navigate(`/credit-block/client/${cid}/${slug}${qs ? `?${qs}` : ''}`, { replace: true });
    },
    [clientId, clientSection, navigate, searchParams]
  );

  useEffect(() => {
    urlSectionAppliedRef.current = null;
  }, [clientId]);

  useEffect(() => {
    if (!clientSection) return;
    if (!isKnownCreditBlockClientSection(clientSection)) {
      navigate(`/credit-block/client/${encodeURIComponent(clientId)}/${CREDIT_BLOCK_CLIENT_DEFAULT_SECTION}`, {
        replace: true,
      });
      return;
    }
    const tabId = creditBlockClientSectionToTabId(clientSection);
    if (!tabId) return;
    const marker = `${clientId}:${clientSection}`;
    if (urlSectionAppliedRef.current === marker) return;
    urlSectionAppliedRef.current = marker;
    setActiveTab(tabId);
    setTabNavigationRequest({ tabId, nonce: Date.now() });
  }, [clientSection, clientId, navigate, setActiveTab, setTabNavigationRequest]);

  useEffect(() => {
    const sid = searchParams.get('subscriptionId')?.trim();
    if (clientSection === 'subscription' && sid) setSelectedSubscriptionId(sid);
  }, [clientSection, searchParams, setSelectedSubscriptionId]);

  return {
    clientSection,
    searchParams,
    navigate,
    syncTabSlugInUrl,
  };
}
